import React, { useEffect, useMemo, useState } from "react";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CircularProgress, IconButton, Button as MuiButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Button from "@components/Button/Button";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import type { DrawerType } from "@components/SideDrawer/SideDrawer.types";
import { useNotification } from "@context/NotificationContext.tsx";
import AddResourceDrawer from "@features/vessel-call/vessel-call-status/MovementServices/form/closeMovement/AddResourceDrawer.tsx";
import EditResourceDrawer from "@features/vessel-call/vessel-call-status/MovementServices/form/closeMovement/EditResourceDrawer.tsx";
import type { Resource } from "@features/vessel-call/vessel-call-status/MovementServices/form/CloseMovementService.tsx";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { SideDrawerAttachments } from "@services/movement-services/types";
import { getMovementServiceDetailsById } from "@services/movementServicesApi";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { VesselCallResponse } from "@services/vesselCall/types";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import MovementFlowButtons from "../flow/MovementFlowButtons";
import { CLAIMABLE_MOVEMENT_STATUSES } from "../flow/utils";
import type {
  MovementError,
  Resource as ResouceForAType,
  ResourceType,
  VesselCondition,
} from "../form/utils";
import { useAddResource } from "../hooks/useAddResource";
import { useDeleteResource } from "../hooks/useDeleteResource";
import { useEditResource } from "../hooks/useEditResource";
import DetailsMovementDrawer from "./components/DetailsMovementDrawer";
import MarineInformation from "./components/MarineInformation";
import MovementDocumentsSection from "./components/MovementDocumentsSection/MovementDocumentsSection";
import MovementRemarksSection from "./components/MovementRemarksSection";
import MovementServiceDetailsSection from "./components/MovementServiceDetailsSection/MovementServiceDetailsSection";
import { STATUS_TO_BADGE } from "./components/MovementServiceSummary";
import TerminalInformation from "./components/TerminalInformation";
import { useDownloadMovementServiceDocument } from "./hooks/useDownloadMovementServiceDocument";
import {
  mapBackendResponseToApi,
  mapMovementServiceDetails,
} from "./movementServiceDetails.mapper";
import {
  mapMovementServiceStatus,
  MOVEMENT_SERVICE_STATUS_TO_BADGE,
} from "./movementServiceStatus.mapper";
import type { MovementServiceDocument, MovementServiceStatusDetails } from "./types";

const getLatestTerminalDecision = (decisions: MovementServiceStatusDetails[], status: string) =>
  decisions.reduce<MovementServiceStatusDetails | undefined>((latest, decision) => {
    if (decision.status !== status || decision.submittedByRole !== "terminal") return latest;

    const timestamp = Date.parse(decision.timestamp ?? "") || 0;
    const latestTimestamp = Date.parse(latest?.timestamp ?? "") || 0;
    return !latest || timestamp >= latestTimestamp ? decision : latest;
  }, undefined);

const hasTerminalInformation = (decision?: MovementServiceStatusDetails) =>
  Boolean(decision?.externalNotes?.trim() || decision?.externalAttachments?.length);

interface OutletContextType {
  handleOpenDrawer: (drawerName: string) => void;
  fakeStatus?: string | null;
  setFakeStatus?: (status: string | null) => void;
  vesselCallDetails?: VesselCallResponse;
}

const MovementServicePreview: React.FC = () => {
  const { subModuleId } = useParams<{ subModuleId: string }>();
  const movementServiceId = subModuleId; // Route uses :subModuleId
  const service = "bff-vessel-call";

  const { t } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleOpenDrawer, fakeStatus, vesselCallDetails } = useOutletContext<OutletContextType>();
  const [drawerType, setDrawerType] = useState<DrawerType | null>(null);
  const isShippingAgentUser = useAuthStore((state) => state.isShippingAgentUser);
  const isMarineDepartmentUser = useAuthStore((state) => state.isMarineDepartmentUser);

  const [isAddResourceDrawerOpen, setIsAddResourceDrawerOpen] = useState(false);
  const [isEditResourceDrawerOpen, setIsEditResourceDrawerOpen] = useState(false);
  const [editingResourceIndex, setEditingResourceIndex] = useState<number | null>(null);

  const theme = useTheme();
  const { handleDownload: downloadDocument, handlePreview: previewDocument } =
    useDownloadMovementServiceDocument({ movementId: movementServiceId });

  // Fetch vessel conditions for mapping codes to names
  const { options: vesselConditions } = useFormattedReference<VesselCondition>(
    REFERENCE_DATA_TYPES.VESSEL_CONDITIONS,
    "bff-vessel-call",
  );

  // Fetch resourceTypes for mapping codes to names
  const { options: resourceTypes } = useFormattedReference<ResourceType>(
    REFERENCE_DATA_TYPES.RESOURCE_TYPES,
    "bff-vessel-call",
  );

  // Create a map of vessel condition codes to names
  const vesselConditionsMap = useMemo(() => {
    const map = new Map<string, string>();
    vesselConditions.forEach((condition) => {
      map.set(condition.conditionCode, condition.conditionDesc);
    });
    return map;
  }, [vesselConditions]);

  const resourceTypesMap = useMemo(() => {
    const map = new Map<string, string>();
    resourceTypes.forEach(({ resourceTypeCode, resourceTypeDesc }) => {
      map.set(resourceTypeCode, resourceTypeDesc);
    });
    return map;
  }, [resourceTypes]);

  const { options: resources } = useFormattedReference<ResouceForAType>(
    REFERENCE_DATA_TYPES.RESOURCE,
    "bff-vessel-call",
  );

  const resourcesMap = useMemo(() => {
    const map = new Map<string, string>();
    resources.forEach(({ resourceCode, resourceName }) => {
      map.set(resourceCode, resourceName);
    });
    return map;
  }, [resources]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      "movement-service-preview",
      movementServiceId,
      vesselConditionsMap.size,
      isShippingAgentUser,
    ],
    queryFn: async () => {
      if (!movementServiceId) {
        throw new Error("Movement service ID is required");
      }

      const backendResponse = await getMovementServiceDetailsById({
        id: movementServiceId,
        service,
      });

      const apiFormat = mapBackendResponseToApi(backendResponse, vesselCallDetails);

      return mapMovementServiceDetails(
        apiFormat,
        vesselConditionsMap,
        resourceTypesMap,
        resourcesMap,
        { showRequestedResourcesOnly: isShippingAgentUser },
        vesselCallDetails,
      );
    },
    enabled: Boolean(movementServiceId),
    staleTime: 0,
    refetchInterval: 60_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Use fakeStatus if available, otherwise use data.status
  const effectiveStatus = fakeStatus || data?.status || "";
  const canManageResources =
    isMarineDepartmentUser && ["APPROVED", "MOVEMENT_STARTED"].includes(effectiveStatus);

  const { addResource } = useAddResource({
    handleSuccess: () => {
      setIsAddResourceDrawerOpen(false);
      void refetch();
    },
    handleError: (error: MovementError) => {
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const { editResource } = useEditResource({
    handleSuccess: () => {
      setIsEditResourceDrawerOpen(false);
      setEditingResourceIndex(null);
      void refetch();
    },
    handleError: (error: MovementError) => {
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const { deleteResource } = useDeleteResource({
    handleSuccess: () => {
      setIsEditResourceDrawerOpen(false);
      setEditingResourceIndex(null);
      void refetch();
    },
    handleError: (error: MovementError) => {
      notify({
        title: t("common.error"),
        description:
          error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const movementFlowData = data
    ? {
        id: data.id,
        status: effectiveStatus,
        submittedByRole: data.submittedByRole,
        task: data.task
          ? {
              status: data.task.status,
              claimOwnerName: data.task.claimOwnerName,
              taskId: data.task.taskId,
              role: data.task.role,
              workflowId: data.task.workflowId,
              runId: data.task.runId,
            }
          : null,
        terminalReport: data.terminalReport ?? null,
        to: data.to,
        from: data.from,
        associatedPortService: data.associatedPortService,
      }
    : undefined;

  const areButtonsVisible = Boolean(
    effectiveStatus && CLAIMABLE_MOVEMENT_STATUSES.includes(effectiveStatus),
  );

  const handleBack = () => {
    const backPath = location.pathname.replace(/(\/movement-services).*/, "$1");
    navigate(backPath);
  };

  // Helper function to find a document by ID across all document groups
  const findDocumentById = (documentId: string): MovementServiceDocument | undefined => {
    if (!data?.documentGroups) return undefined;

    for (const group of data.documentGroups) {
      const doc = group.documents.find((d) => d.id === documentId);
      if (doc) return doc;
    }
    return undefined;
  };

  const isValidUrl = (url: string | undefined): boolean => {
    if (!url) return false;
    return /^https?:\/\//.test(url);
  };

  const handleDownload = (documentId: string) => {
    const document = findDocumentById(documentId);
    if (document?.url && isValidUrl(document.url)) {
      downloadDocument(service, document.url, document.fileName);
    } else {
      console.error("Document not found, URL missing, or invalid URL:", documentId, document?.url);
    }
  };

  const handlePreview = (documentId: string) => {
    const document = findDocumentById(documentId);
    if (document?.url && isValidUrl(document.url)) {
      previewDocument(service, document.url);
    } else {
      console.error("Document not found, URL missing, or invalid URL:", documentId, document?.url);
    }
  };

  const handleAddResource = async (resource: Resource) => {
    if (!movementServiceId) return;

    await addResource({
      service,
      movementId: movementServiceId,
      payload: resource,
    });
  };

  const handleEditResource = async (index: number, resource: Resource) => {
    if (!movementServiceId) return;

    const resourceId = data?.additionalResources?.[index]?.id;
    if (!resourceId) return;

    await editResource({
      service,
      movementId: movementServiceId,
      resourceId,
      payload: resource,
    });
  };

  const handleRemoveResource = async (index: number) => {
    if (!movementServiceId) return;

    const resourceId = data?.additionalResources?.[index]?.id;
    if (!resourceId) return;

    await deleteResource({
      service,
      movementId: movementServiceId,
      resourceId,
    });
  };

  const hasViewDetails =
    (data?.statusDetails?.[0]
      ? [
          data.statusDetails[0].rejectionReason,
          data.statusDetails[0].internalNotes,
          data.statusDetails[0].externalNotes,
          data.statusDetails[0].comments,
          data.statusDetails[0].rejectionReasonAttachments,
          data.statusDetails[0].internalAttachments,
          data.statusDetails[0].externalAttachments,
          data.statusDetails[0].berthPlan,
          data.statusDetails[0].unberthPlan,
        ].some((value) => (Array.isArray(value) ? value.length > 0 : value))
      : false) ||
    (data?.cancelReasonStatus?.cancelReason !== null &&
      data?.cancelReasonStatus?.cancelReason !== undefined);

  useEffect(() => {
    if (!hasViewDetails && drawerType === "details") {
      setDrawerType(null);
    }
  }, [hasViewDetails, drawerType]);

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-10"
        data-testid="movement-service-preview-loading"
      >
        <CircularProgress />
      </div>
    );
  }

  if (isError && !data) {
    return (
      <div data-testid="movement-service-preview-error">
        <ErrorHandler error={error} testId="movement-service-preview-error-handler" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const statusMapping = mapMovementServiceStatus(effectiveStatus);
  const badgeType =
    MOVEMENT_SERVICE_STATUS_TO_BADGE[effectiveStatus] ??
    STATUS_TO_BADGE[effectiveStatus] ??
    "warning";
  const statusDotColor =
    badgeType === "success"
      ? theme.palette.success.main
      : badgeType === "error"
        ? theme.palette.error.main
        : theme.palette.warning.main;
  const displayStatusText = t(statusMapping.translationKey, { defaultValue: effectiveStatus });

  const sections = data.sections ?? [];
  const additionalInfoIndex = sections.findIndex((section) => section.id === "additional-info");
  const timesIndex = sections.findIndex((section) => section.id === "times");

  const terminalInformationIndex =
    additionalInfoIndex >= 0
      ? additionalInfoIndex + 1
      : timesIndex >= 0
        ? timesIndex
        : sections.length;
  const marineInformationIndex = timesIndex >= 0 ? timesIndex : sections.length;

  const arrivalDecision = getLatestTerminalDecision(data.statusDetails ?? [], "APPROVED");
  const departureDecision = getLatestTerminalDecision(data.statusDetails ?? [], "NON_DISPUTED");
  const showDepartureInformation = Boolean(
    departureDecision && (hasTerminalInformation(departureDecision) || data.unberthPlan?.length),
  );
  const showArrivalInformation = Boolean(
    arrivalDecision && (hasTerminalInformation(arrivalDecision) || data.berthPlan?.length),
  );
  const isBerthToBerth = data.movementType === "BERTH_TO_BERTH";
  const handleTerminalDownload = (attachment: SideDrawerAttachments) => {
    downloadDocument(service, attachment.url, attachment.originalFileName);
  };
  const handleTerminalPreview = (attachment: SideDrawerAttachments) => {
    previewDocument(service, attachment.url);
  };
  const terminalInformationSections = (
    <>
      {showDepartureInformation && (
        <TerminalInformation
          title={t(
            `vessel-call.movement-services.preview.terminal-information.${isBerthToBerth ? "departure" : "title"}`,
          )}
          note={departureDecision?.externalNotes}
          attachments={departureDecision?.externalAttachments}
          unberthPlan={data.unberthPlan}
          testId="movement-service-terminal-information-departure"
          onDownload={handleTerminalDownload}
          onPreview={handleTerminalPreview}
        />
      )}
      {showArrivalInformation && (
        <TerminalInformation
          title={t(
            `vessel-call.movement-services.preview.terminal-information.${isBerthToBerth ? "arrival" : "title"}`,
          )}
          note={arrivalDecision?.externalNotes}
          attachments={arrivalDecision?.externalAttachments}
          berthPlan={data.berthPlan}
          testId="movement-service-terminal-information-arrival"
          onDownload={handleTerminalDownload}
          onPreview={handleTerminalPreview}
        />
      )}
    </>
  );

  const marineInformationSection = (
    <MarineInformation
      statusDetails={data.statusDetails ?? []}
      onDownload={handleTerminalDownload}
      onPreview={handleTerminalPreview}
    />
  );

  const resourceSections = data.resourceSections ?? [];
  const documentGroups = data.documentGroups ?? [];
  const nonCertificateDocuments = documentGroups.filter((group) => group.id !== "certificates");
  const editingResource =
    editingResourceIndex !== null ? data.additionalResources?.[editingResourceIndex] : undefined;

  return (
    <div className="flex flex-col gap-6" data-testid="movement-service-preview">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-start">
            <IconButton
              size="medium"
              sx={{ color: "text.primary", mr: 1 }}
              onClick={handleBack}
              data-testid="movement-service-preview-back-button"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>

            <div>
              <Typography
                variant="h5"
                data-testid="movement-service-preview-subtitle"
                sx={{ color: "text.primary" }}
              >
                {`${t("vessel-call.movement-services.preview-title")} - ${data.requestNumber}`}
              </Typography>

              <div className="flex flex-wrap items-center gap-3 mt-2 ml-1 text-sm">
                <div className="flex items-center">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: statusDotColor }}
                    />
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: theme.typography.fontWeightMedium,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {displayStatusText}
                    </Typography>
                  </div>
                  {hasViewDetails && (
                    <>
                      <span className="text-base ml-2">–</span>
                      <Button
                        testId={`movement-service-section-view-details`}
                        variant="text"
                        type="button"
                        className="ml-4 whitespace-nowrap"
                        onClick={() => setDrawerType("details")}
                        label={
                          <span className="normal-case font-medium">
                            {t(`vessel-call.movement-services.preview.view-details`)}
                          </span>
                        }
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {areButtonsVisible && movementFlowData ? (
          <MovementFlowButtons openDrawer={handleOpenDrawer} movementFlowData={movementFlowData} />
        ) : null}
      </div>
      <div className="flex flex-col gap-4">
        {sections.map((section, index) => (
          <React.Fragment key={section.id}>
            {index === terminalInformationIndex && terminalInformationSections}
            {index === marineInformationIndex && marineInformationSection}
            <MovementServiceDetailsSection
              section={section}
              testId={`movement-service-section-${section.id}`}
              movementDetails={section.id === "additional-info" ? movementFlowData : undefined}
            />
          </React.Fragment>
        ))}
        {terminalInformationIndex === sections.length && terminalInformationSections}
        {marineInformationIndex === sections.length && marineInformationSection}
      </div>
      {
        <div className="flex flex-col gap-4">
          {resourceSections.length || canManageResources ? (
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <Typography variant="subtitle1" className="text-[#111827]">
                  {t("vessel-call.movement-services.preview.cards.additional-resources")}
                </Typography>
                {canManageResources && (
                  <div id="movement-service-section-add-resource-button">
                    <MuiButton
                      variant="outlined"
                      onClick={() => setIsAddResourceDrawerOpen(true)}
                      sx={{ textTransform: "none" }}
                      startIcon={<AddCircleOutlineOutlinedIcon />}
                    >
                      {t("vessel-call.movement-services.buttons.add-resource")}
                    </MuiButton>
                  </div>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {resourceSections.map((section, index) => (
                  <MovementServiceDetailsSection
                    key={section.id}
                    section={section}
                    testId={`movement-service-resource-${section.id}`}
                    showActions={canManageResources}
                    onEdit={() => {
                      setEditingResourceIndex(index);
                      setIsEditResourceDrawerOpen(true);
                    }}
                    onRemove={() => handleRemoveResource(index)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {nonCertificateDocuments.length
            ? nonCertificateDocuments.map((group) => (
                <MovementDocumentsSection
                  key={group.id}
                  documents={group.documents}
                  titleKey={group.titleKey}
                  testId={`movement-documents-${group.id}`}
                  onDownload={handleDownload}
                  onPreview={handlePreview}
                  hideWrapper
                />
              ))
            : null}
        </div>
      }
      {data.remarksSection && <MovementRemarksSection section={data.remarksSection} />}
      <DetailsMovementDrawer
        isOpen={drawerType === "details"}
        onClose={() => setDrawerType(null)}
        statusDetails={data.statusDetails}
      />
      <AddResourceDrawer
        isOpen={isAddResourceDrawerOpen}
        onClose={() => setIsAddResourceDrawerOpen(false)}
        onAdd={handleAddResource}
        resourceTypes={resourceTypes}
        resources={resources}
        ats={data.timeEstimates.ats}
        onlyResourceFieldsRequired
      />
      {editingResource && (
        <EditResourceDrawer
          isOpen={isEditResourceDrawerOpen}
          onClose={() => {
            setIsEditResourceDrawerOpen(false);
            setEditingResourceIndex(null);
          }}
          onSave={(resource) => {
            if (editingResourceIndex !== null) {
              handleEditResource(editingResourceIndex, resource);
            }
          }}
          resource={{
            additionalResourceType: editingResource.resourceType,
            numberOfResources: editingResource.quantity,
            resourceName: editingResource.resourceName ?? undefined,
            startTimeOfUsage: editingResource.startTimeOfUsage ?? undefined,
            endTimeOfUsage: editingResource.endTimeOfUsage ?? undefined,
            connectionTime: editingResource.connectionTime ?? undefined,
            location: editingResource.location ?? undefined,
            id: editingResource.id,
          }}
          resourceTypes={resourceTypes}
          resources={resources}
          ats={data.timeEstimates.ats}
          showRemoveButton={false}
          onlyResourceFieldsRequired
        />
      )}
    </div>
  );
};

export default MovementServicePreview;
